/**
 * cutoutUtils.js
 * ----------------------------------------------------------------------------
 * RECORTES VINCULADOS à peça (cuba, cooktop, furos...).
 *
 * Diferente dos `elements` livres (px absolutos do canvas), o recorte vive
 * DENTRO do item da ficha_tecnica (`item.cutouts`) com coordenadas em METROS
 * relativas ao canto superior-esquerdo da peça:
 *   { id, kind, cx, cy, width, height, diameter, rotation, label }
 *
 * Vantagens desse modelo (o mesmo das pranchas de marmoraria):
 *  - o recorte acompanha a peça ao mover/redimensionar;
 *  - a cota automática "eixo do recorte -> borda da peça" é derivável;
 *  - Forma Pronta consegue guardar recortes no template.
 *
 * NÃO altera preço: recorte é informação de produção; a cobrança do serviço
 * continua sendo um item "Acabamento" separado, como hoje.
 */

import { uid } from "./annotationUtils";
import { formatMeasure } from "./measurementUtils";

/** Catálogo de recortes (medidas reais default, em metros). */
export const CUTOUT_TYPES = [
  { kind: "sink", label: "Cuba", icon: "mdi-inbox-outline", width: 0.5, height: 0.4 },
  { kind: "sink_double", label: "Cuba dupla", icon: "mdi-inbox-multiple-outline", width: 0.8, height: 0.4 },
  { kind: "cooktop", label: "Cooktop", icon: "mdi-stove", width: 0.58, height: 0.51 },
  { kind: "tap_hole", label: "Furo de torneira", icon: "mdi-water-pump", diameter: 0.035 },
  { kind: "hole", label: "Furo", icon: "mdi-record-circle-outline", diameter: 0.1 },
  { kind: "rect", label: "Recorte retangular", icon: "mdi-crop-square", width: 0.3, height: 0.2 },
  { kind: "circle", label: "Recorte circular", icon: "mdi-circle-outline", diameter: 0.15 }
];

const DEFAULT_LABELS = {
  sink: "CUBA",
  sink_double: "CUBA DUPLA",
  cooktop: "COOKTOP",
  tap_hole: "TORNEIRA",
  hole: "FURO",
  rect: "RECORTE",
  circle: "RECORTE"
};

export function getCutoutMeta(kind) {
  return CUTOUT_TYPES.find(c => c.kind === kind) || CUTOUT_TYPES[0];
}

/** Símbolos circulares usam diâmetro; os demais, largura × altura. */
export function isCircularKind(kind) {
  return ["hole", "tap_hole", "circle"].includes(kind);
}

/**
 * Converte um INSUMO type "Furo" (cadastrado no Insumos) nas propriedades
 * de um recorte do desenho.
 * furo_config: { kind, unidade: 'cm'|'mm', largura, altura, diametro }
 * (compat: schema antigo `largura_cm`/`altura_cm`/`diametro_cm` em cm)
 * @returns {{ kind, label, displayUnit, width?, height?, diameter? }}
 */
export function cutoutFromInsumo(insumo) {
  const config = (insumo && insumo.furo_config) || {};
  // sem kind salvo => furo genérico (nunca "cai" no primeiro do catálogo)
  const meta = getCutoutMeta(config.kind || "hole");
  const divisor = config.unidade === "mm" ? 1000 : 100;
  const toMeters = (value, legacyCm) => {
    let n = parseFloat(`${value}`.replace(",", "."));
    if (Number.isFinite(n) && n > 0) return n / divisor;
    n = parseFloat(`${legacyCm}`.replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n / 100 : null;
  };
  const result = {
    kind: meta.kind,
    label: `${insumo.nome || meta.label}`.toUpperCase(),
    // A geometria continua em metros; esta propriedade preserva somente a
    // unidade escolhida no cadastro para exibição e edição no desenho.
    displayUnit: config.unidade === "mm" ? "mm" : "cm"
  };
  if (isCircularKind(meta.kind)) {
    result.diameter =
      toMeters(config.diametro, config.diametro_cm) || meta.diameter || 0.1;
  } else {
    result.width =
      toMeters(config.largura, config.largura_cm) || meta.width || 0.3;
    result.height =
      toMeters(config.altura, config.altura_cm) || meta.height || 0.2;
  }
  return result;
}

export function isCircularCutout(cutout) {
  return !!(cutout && cutout.diameter);
}

/** Dimensões efetivas (m) do recorte — círculos usam o diâmetro. */
export function getCutoutSize(cutout) {
  if (isCircularCutout(cutout)) {
    return { w: cutout.diameter, h: cutout.diameter };
  }
  return { w: cutout.width || 0.1, h: cutout.height || 0.1 };
}

/**
 * Cria um recorte novo.
 * @param {string} kind ver CUTOUT_TYPES
 * @param {object} overrides { cx, cy, ... } em METROS relativos à peça
 */
export function createCutout(kind, overrides = {}) {
  const meta = getCutoutMeta(kind);
  const base = {
    id: uid("cutout"),
    kind: meta.kind,
    cx: overrides.cx != null ? overrides.cx : 0.3,
    cy: overrides.cy != null ? overrides.cy : 0.3,
    rotation: overrides.rotation || 0,
    displayUnit: overrides.displayUnit === "mm" ? "mm" : "cm",
    label: overrides.label != null ? overrides.label : DEFAULT_LABELS[meta.kind] || "RECORTE"
  };
  if (meta.diameter) {
    base.diameter = overrides.diameter != null ? overrides.diameter : meta.diameter;
  } else {
    base.width = overrides.width != null ? overrides.width : meta.width;
    base.height = overrides.height != null ? overrides.height : meta.height;
  }
  return base;
}

/**
 * Garante que o recorte fica DENTRO da peça (com folga mínima de 1cm da
 * borda). pieceMeters = { w, h } da peça.
 */
export function clampCutoutToPiece(cutout, pieceMeters) {
  const size = getCutoutSize(cutout);
  const margin = 0.01;
  const halfW = size.w / 2;
  const halfH = size.h / 2;
  const minX = halfW + margin;
  const maxX = Math.max(minX, (pieceMeters.w || 0) - halfW - margin);
  const minY = halfH + margin;
  const maxY = Math.max(minY, (pieceMeters.h || 0) - halfH - margin);
  return {
    ...cutout,
    cx: Math.min(maxX, Math.max(minX, cutout.cx)),
    cy: Math.min(maxY, Math.max(minY, cutout.cy))
  };
}

/**
 * Converte o recorte para px do canvas usando os bounds visuais da peça.
 * Usa PROPORÇÃO (e não escala absoluta) para respeitar a espessura visual
 * mínima aplicada a peças finas.
 * @returns {{cx, cy, w, h, pxPerMeterX, pxPerMeterY}}
 */
export function cutoutToPx(cutout, piece, bounds) {
  const pw = piece.width || 1;
  const ph = piece.height && piece.height > 0.1 ? piece.height : 0.1;
  const pxPerMeterX = bounds.w / pw;
  const pxPerMeterY = bounds.h / ph;
  const size = getCutoutSize(cutout);
  return {
    cx: bounds.x + cutout.cx * pxPerMeterX,
    cy: bounds.y + cutout.cy * pxPerMeterY,
    w: size.w * pxPerMeterX,
    h: size.h * pxPerMeterY,
    pxPerMeterX,
    pxPerMeterY
  };
}

/** Rótulo de medida do recorte: "50×40cm" ou "Ø10cm". */
export function getCutoutSizeLabel(cutout) {
  const unit = cutout && cutout.displayUnit === "mm" ? "mm" : "cm";
  const factor = unit === "mm" ? 1000 : 100;
  const format = value =>
    `${Number(((Number(value) || 0) * factor).toFixed(2))}`.replace(".", ",");
  if (isCircularCutout(cutout)) {
    return `Ø${format(cutout.diameter)}${unit}`;
  }
  return `${format(cutout.width)}×${format(cutout.height)}${unit}`;
}

/**
 * Cotas automáticas do EIXO do recorte até as bordas da peça (padrão de
 * prancha de marmoraria): horizontal acima da peça (borda esquerda -> eixo)
 * e vertical à esquerda (borda de trás -> eixo).
 * Mesmo formato das cotas de measurementUtils ({extLines, dimLine, label}).
 */
export function getCutoutAxisDimensions(cutout, piece, bounds, opts = {}) {
  const px = cutoutToPx(cutout, piece, bounds);
  const offset = opts.offset != null ? opts.offset : 16;
  const gap = 3;
  const over = 4;

  const topY = bounds.y - offset; // linha de cota horizontal (acima da peça)
  const leftX = bounds.x - offset; // linha de cota vertical (à esquerda)

  return {
    horizontal: {
      extLines: [
        { x1: bounds.x, y1: bounds.y - gap, x2: bounds.x, y2: topY - over },
        { x1: px.cx, y1: px.cy, x2: px.cx, y2: topY - over }
      ],
      dimLine: { x1: bounds.x, y1: topY, x2: px.cx, y2: topY },
      label: {
        x: (bounds.x + px.cx) / 2,
        y: topY - 4,
        text: `${formatMeasure(cutout.cx)}m`,
        rotate: 0
      }
    },
    vertical: {
      extLines: [
        { x1: bounds.x - gap, y1: bounds.y, x2: leftX - over, y2: bounds.y },
        { x1: px.cx, y1: px.cy, x2: leftX - over, y2: px.cy }
      ],
      dimLine: { x1: leftX, y1: bounds.y, x2: leftX, y2: px.cy },
      label: {
        x: leftX - 5,
        y: (bounds.y + px.cy) / 2,
        text: `${formatMeasure(cutout.cy)}m`,
        rotate: -90
      }
    }
  };
}

/**
 * COTA ENCADEADA (progressiva) ao longo do eixo X, quando a peça tem 2+
 * recortes: borda esquerda -> eixo 1 -> eixo 2 -> ... -> borda direita.
 * Desenhada acima da peça, como na prancha de referência.
 * @returns {Array<{extLines, dimLine, label}>} um segmento por trecho
 */
export function getChainDimensions(cutouts, piece, bounds, opts = {}) {
  const list = (cutouts || []).filter(Boolean);
  if (list.length < 2) return [];
  const offset = opts.offset != null ? opts.offset : 16;
  const gap = 3;
  const over = 4;
  const topY = bounds.y - offset;

  // pontos do encadeamento em METROS (0 -> eixos ordenados -> comprimento)
  const stops = [0]
    .concat(
      list
        .map(c => c.cx)
        .sort((a, b) => a - b)
    )
    .concat([piece.width]);

  const pxPerMeterX = bounds.w / (piece.width || 1);
  const segments = [];
  for (let i = 0; i < stops.length - 1; i++) {
    const fromM = stops[i];
    const toM = stops[i + 1];
    const span = toM - fromM;
    if (span <= 0.001) continue;
    const x1 = bounds.x + fromM * pxPerMeterX;
    const x2 = bounds.x + toM * pxPerMeterX;
    segments.push({
      extLines: [
        { x1, y1: bounds.y - gap, x2: x1, y2: topY - over },
        { x1: x2, y1: bounds.y - gap, x2: x2, y2: topY - over }
      ],
      dimLine: { x1, y1: topY, x2, y2: topY },
      label: {
        x: (x1 + x2) / 2,
        y: topY - 4,
        text: `${formatMeasure(span)}m`,
        rotate: 0
      }
    });
  }
  return segments;
}
